/**
 * The verifications inbox's controller: `verifications.model.ts` wired to the
 * three reads (`fetchVerificationRequests`, `fetchAffiliationClaims`,
 * `fetchRepresentationRequests`), the documents behind them, and the writes
 * — `answerVerificationRequest`, `referVerificationRequest`,
 * `answerAffiliationClaim`, `answerRepresentationRequest`.
 *
 * It owns what the model cannot: the three lists as they arrive, the documents
 * keyed by entry, the bulk selection, the busy and error pair, the signed
 * image map, the shared viewer, and the filters — stored beside the focus they
 * were chosen under and compared during render, so a link opened onto an
 * already-mounted screen can widen them without an effect.
 *
 * Every row of a bulk answer is attempted. A refusal on one is a fact about
 * that row and says nothing about the rest; `bulkRefusalReport` names them.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type {
  AffiliationClaim,
  AttributionFeeCorrection,
  AttributionServiceCase,
  ProvenanceDocument,
  RepresentationAnswer,
  RepresentationRequestReceived,
  VerificationAnswer,
  VerificationRequestSummary,
} from "@ondo/shared";

import { ApiError } from "../lib/api.ts";
import {
  answerAffiliationClaim,
  answerRepresentationRequest,
  answerVerificationRequest,
  assignAttributionReviewer,
  correctAttributionFee,
  extendAttributionTarget,
  fetchAffiliationClaims,
  fetchAttributionServiceCases,
  fetchAttributionServiceLog,
  fetchProvenanceDocuments,
  fetchRepresentationRequests,
  fetchVerificationRequests,
  recordAttributionFeePaid,
  referVerificationRequest,
  requestAttributionEvidence,
} from "../lib/api.ts";
import {
  signProvenanceDocuments,
  uploadProvenanceDocuments,
} from "../lib/provenanceDocuments.ts";
import { signWorkImages } from "../lib/workImages.ts";
import { useSpotlight } from "../spotlight/useSpotlight.ts";
import type { SpotlightSource } from "../spotlight/spotlight.model.ts";
import type { MeResponse } from "@ondo/shared";
import { toCaseCard, toLogLine } from "./attributionService.model.ts";
import type { LogLine } from "./attributionService.model.ts";
import {
  awaitingCount,
  bulkRefusal,
  bulkRefusalReport,
  cardTitle,
  capacitiesFor,
  documentsToOpen,
  emptyFor,
  refusalFor,
  toCard,
  toClaimCard,
  toRepresentationCard,
  visibleCards,
} from "./verifications.model.ts";
import type { DocumentRow, Focus, VerificationsView } from "./verifications.model.ts";
import { EMPTY_COPY } from "./verifications.constants.ts";
import type {
  AspectFilter,
  StatusFilter,
  VerificationTab,
} from "./verifications.constants.ts";

/**
 * The filter state, tagged with the focus it was chosen under.
 *
 * The tag is what lets a stale set be spotted during render rather than
 * corrected in an effect. See the comment where it is compared.
 */
interface Filters {
  focusKey: string | null;
  tab: VerificationTab;
  status: StatusFilter;
  aspect: AspectFilter;
}

/** Where a focus — or the absence of one — puts the three filters. */
function filtersFor(focusKey: string | null): Filters {
  return {
    focusKey,
    // The tab a focus implies, inferred from which kind it is rather than
    // carried separately, so there is one authority for it.
    tab: focusKey?.startsWith("claim:")
      ? "affiliations"
      : focusKey?.startsWith("representation:")
        ? "representation"
        : "requests",
    status: "all",
    aspect: "all",
  };
}

/** What one load returns, before any of it reaches state. */
interface Loaded {
  requests: VerificationRequestSummary[];
  claims: AffiliationClaim[];
  representation: RepresentationRequestReceived[];
  documents: Record<string, ProvenanceDocument[]>;
  /** OND-60's cases, from whichever side the reader is on. */
  cases: AttributionServiceCase[];
}

interface Input {
  me: MeResponse | null;
  /**
   * The workspace's own principal, so the inbox is scoped to it.
   *
   * A person who also belongs to a gallery holds two workspaces, and a request
   * addressed to the gallery has no business in their personal collector inbox.
   * Null means the personal one, which is the caller's own ONDO id.
   */
  principalOndoId: string | null;
  /**
   * The row a link asked for. Null when the screen was opened plainly.
   *
   * Read off the URL by the view, so a reload keeps it and the back button
   * works — the same argument `useCollectorController` makes for the
   * destination being a segment rather than state.
   */
  focus: Focus | null;
}

/**
 * A refusal this screen has already written a sentence for.
 *
 * `run` reports every failure through `refusalFor`, which turns a status into
 * one of five sentences — right for a single write, wrong for a bulk answer
 * where the useful thing is which rows did not land. Throwing this says "the
 * message is the message".
 */
class ReportedRefusal extends Error {}

export function useVerificationsController({ me, principalOndoId, focus }: Input) {
  const [requests, setRequests] = useState<VerificationRequestSummary[] | null>(null);
  const [claims, setClaims] = useState<AffiliationClaim[]>([]);
  const [representation, setRepresentation] = useState<RepresentationRequestReceived[]>([]);
  const [cases, setCases] = useState<AttributionServiceCase[]>([]);
  /**
   * The service log, keyed by request and fetched only when a reader opens one.
   *
   * THE ONE READ ON THIS SCREEN THAT IS NOT IN THE INITIAL LOAD, and the
   * asymmetry is deliberate. The tab counts come off the cases, so those have to
   * arrive with everything else; a log is three or four lines a reader asks for
   * about one case, and fetching every case's log to render a list would be a
   * query per row for something nobody has looked at.
   */
  const [caseLog, setCaseLog] = useState<Record<string, LogLine[]>>({});
  // Keyed by entry, because that is what the documents hang off — two requests
  // about the same link share one load rather than fetching it twice.
  const [documents, setDocuments] = useState<Record<string, ProvenanceDocument[]>>({});

  const [selected, setSelected] = useState<string[]>([]);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  /**
   * The three filters, and which focus they were chosen under.
   *
   * One state object rather than three, because arriving on a row has to move
   * all of them at once and they are only ever read together.
   *
   * THE FOCUS IS STORED BESIDE THEM AND COMPARED DURING RENDER, which is the
   * pattern `useMe` uses to derive `loading` from a userId mismatch, and it is
   * here for the same reason: resetting a filter in an effect because a prop
   * changed is a cascading render, and the linter says so. A mismatch means the
   * URL has just spoken, so the filters read as what shows the row it named
   * rather than as what the last visit left behind.
   *
   * It matters only for a focus arriving at a screen already mounted — a second
   * notification opened from the inbox. On a cold load the defaults are already
   * wide enough. Without it, a link to an answered request would land on a
   * reader whose filter was still "Awaiting you" and show them nothing.
   */
  const focusKey = focus ? `${focus.kind}:${focus.id}` : null;
  const [chosen, setChosen] = useState<Filters>(() => filtersFor(focusKey));
  const filters = chosen.focusKey === focusKey ? chosen : filtersFor(focusKey);
  const { tab, status, aspect } = filters;

  const setTab = (next: VerificationTab) => setChosen({ ...filters, tab: next });
  const setStatus = (next: StatusFilter) => setChosen({ ...filters, status: next });
  const setAspect = (next: AspectFilter) => setChosen({ ...filters, aspect: next });

  /**
   * The shared viewer, so evidence attached to a request can be READ.
   *
   * Somebody is being asked to put their name to a fact, and the documents are
   * the grounds for it — a list of filenames asks them to attest to paperwork
   * they were never shown. The same overlay the provenance library opens, so a
   * verifier who follows the request through to the work sees one viewer.
   */
  const spotlight = useSpotlight();
  // Destructured, because `useSpotlight` returns a fresh object each render and
  // a callback depending on the whole of it would be rebuilt on every one.
  const { open: openSpotlight } = spotlight;

  /**
   * FETCHES AND RETURNS; it sets no state.
   *
   * The split the other controllers keep — `useWorkController`'s effects call a
   * fetch and set state in `.then` — so a resolved promise for an input the
   * screen has since moved off can be dropped by the caller rather than having
   * already been written. It also keeps the effect free of a synchronous
   * setState, which is what `react-hooks/set-state-in-effect` is about.
   */
  const load = useCallback(async (): Promise<Loaded> => {
    // Both tabs on one load. The counts sit in the tab strip, so a screen that
    // fetched the second tab only when opened would show it unlabelled until
    // then — and the second call is one query against an indexed column.
    const [inbox, affiliations, asked, serviceCases] = await Promise.all([
      fetchVerificationRequests(principalOndoId),
      // Affiliation claims are a young feature and a deployment may not have
      // them; an empty second tab must not take the first one down with it.
      fetchAffiliationClaims(principalOndoId).catch(() => [] as AffiliationClaim[]),
      // Same bargain for the third, and it is younger still. A deployment whose
      // API predates `/v1/representation-requests` answers 404, and the tab that
      // matters most to this screen must not go down with the newest one.
      fetchRepresentationRequests(principalOndoId).catch(
        () => [] as RepresentationRequestReceived[],
      ),
      // Same bargain again, and this one is the youngest of the four: a
      // deployment whose API predates `/v1/attribution-service/cases` answers
      // 404, and the tab that matters most must not go down with the newest.
      fetchAttributionServiceCases().catch(() => [] as AttributionServiceCase[]),
    ]);

    // Documents for every entry in the inbox, in one pass. Requests naming a
    // document are the ones that need them, but an entry-scoped request shows
    // its evidence too — that is what the asker wants looked at.
    const entryIds = [...new Set(inbox.map((request) => request.entryId))];
    const loaded = await Promise.all(
      entryIds.map((id) =>
        fetchProvenanceDocuments(id)
          // A refusal here is a narrower read than the inbox, not a failure of
          // it. The card renders without the list rather than not at all.
          .catch(() => [] as ProvenanceDocument[])
          .then((rows) => [id, rows] as const),
      ),
    );

    return {
      requests: inbox,
      claims: affiliations,
      representation: asked,
      documents: Object.fromEntries(loaded),
      cases: serviceCases,
    };
  }, [principalOndoId]);

  const apply = useCallback((loaded: Loaded) => {
    setRequests(loaded.requests);
    setClaims(loaded.claims);
    setRepresentation(loaded.representation);
    setDocuments(loaded.documents);
    setCases(loaded.cases);
  }, []);

  useEffect(() => {
    // The `active` guard the other controllers use: a resolved promise for a
    // principal the screen has since moved off must not write state.
    let active = true;

    // Cleared on success rather than before the attempt, so a reload that fails
    // leaves the message that explains why rather than blanking the screen first.
    load()
      .then((loaded) => {
        if (!active) return;
        apply(loaded);
        setLoadError(null);
      })
      .catch((cause: unknown) => {
        if (!active) return;
        setLoadError(cause instanceof ApiError ? cause.message : "Could not load this.");
      });

    return () => {
      active = false;
    };
  }, [load, apply]);

  /**
   * Every write re-reads the whole inbox rather than patching a row.
   *
   * The convention `useWorkController` follows, and it earns its cost here: an
   * answer can close a request, create an attestation, change a document's
   * status and — on a referral — make a row disappear from this account's inbox
   * entirely. A local patch would have to know all four.
   */
  const run = useCallback(
    async (work: () => Promise<unknown>) => {
      setBusy(true);
      setError(null);
      try {
        await work();
        apply(await load());
        setSelected([]);
      } catch (cause: unknown) {
        setError(
          // A message this screen composed itself survives; anything else is
          // an exception nobody wrote a sentence for. Without the first arm,
          // `answerMany`'s per-row report — which already names the rows and
          // the count — would be flattened into the generic fallback, which is
          // the very thing that made a part-refused bulk answer unreadable.
          cause instanceof ReportedRefusal
            ? cause.message
            : cause instanceof ApiError
              ? refusalFor(cause.status, cause.message)
              : "That did not go through.",
        );
        // Re-read even after a refusal. A 403 usually means somebody else moved
        // the row, and leaving the stale card on screen invites a second try at
        // something that is already gone.
        await load().then(apply).catch(() => undefined);
      } finally {
        setBusy(false);
      }
    },
    [load, apply],
  );

  const answer = useCallback(
    (
      requestId: string,
      body: { answer: VerificationAnswer; body?: string | null; onBehalfOf?: string | null },
    ) => run(() => answerVerificationRequest(requestId, body)),
    [run],
  );

  const refer = useCallback(
    (requestId: string, ondoId: string, note: string | null) =>
      run(() => referVerificationRequest(requestId, { ondoId, note })),
    [run],
  );

  /**
   * One case's service log, fetched when a reader opens it.
   *
   * DECLARED BEFORE THE WRITES THAT USE IT, so no `useCallback` below has to
   * reach forward past a `const`.
   *
   * A REFUSAL SHORTENS THE CARD AND DOES NOT BREAK IT, which is the bargain the
   * documents read on this screen already makes: a narrower read failing is not
   * a failure of the list it sits in.
   */
  const refreshLog = useCallback(async (requestId: string) => {
    const steps = await fetchAttributionServiceLog(requestId).catch(() => null);
    if (!steps) return;
    setCaseLog((held) => ({ ...held, [requestId]: steps.map(toLogLine) }));
  }, []);

  const openCaseLog = useCallback(
    (requestId: string) => {
      void refreshLog(requestId);
    },
    [refreshLog],
  );

  /**
   * OND-60's five writes, all through `run`, so each re-reads the whole screen.
   *
   * THE RE-READ EARNS ITS COST HERE AS MUCH AS ANYWHERE. An extension moves the
   * effective target and leaves the original standing; a request for more
   * evidence moves the STAGE without ending anything; an assignment ends one row
   * and inserts another; a correction changes a fee's standing without touching
   * the fee. A local patch would have to know all four, and the server derives
   * every one of them.
   *
   * THE TWO THAT APPEND TO THE LOG REFRESH IT TOO, because the log is a second
   * read `run` knows nothing about — and a reader who has the log open when they
   * ask for evidence would otherwise watch the card change above a list that
   * did not.
   */
  const askEvidence = useCallback(
    (requestId: string, reason: string) =>
      run(async () => {
        await requestAttributionEvidence(requestId, { reason });
        await refreshLog(requestId);
      }),
    [run, refreshLog],
  );

  const extendTarget = useCallback(
    (requestId: string, targetAt: string, reason: string) =>
      run(async () => {
        await extendAttributionTarget(requestId, { targetAt, reason });
        await refreshLog(requestId);
      }),
    [run, refreshLog],
  );

  const assignReviewer = useCallback(
    (requestId: string, reviewer: string, escalate: boolean, reason: string | null) =>
      run(() => assignAttributionReviewer(requestId, { reviewer, escalate, reason })),
    [run],
  );

  const recordFeePaid = useCallback(
    (feeId: string, note: string | null) =>
      run(() => recordAttributionFeePaid(feeId, { note })),
    [run],
  );

  const correctFee = useCallback(
    (
      feeId: string,
      kind: AttributionFeeCorrection,
      reason: string,
      reference: string | null,
    ) => run(() => correctAttributionFee(feeId, { kind, reason, reference })),
    [run],
  );

  const addDocument = useCallback(
    (entryId: string, files: File[]) =>
      run(() =>
        uploadProvenanceDocuments(
          files.map((file) => ({ entryId, file, kind: "other" as const, label: file.name })),
        ),
      ),
    [run],
  );

  /**
   * Opening one of a card's documents.
   *
   * Signing happens HERE and only when somebody asks to look — the argument
   * `useProvenanceSpotlight` makes: an inbox of thirty requests would otherwise
   * sign every attachment on every load, and a URL signed at the moment of use
   * is seconds old rather than having spent its ten minutes sitting in a list.
   *
   * The cards are rebuilt from the loaded rows rather than read off the view,
   * so this callback does not change identity on every render.
   */
  const openDocument = useCallback(
    (requestId: string, documentId: string) => {
      const cards = (requests ?? []).map((request) =>
        toCard(request, documents[request.entryId]),
      );
      const found = documentsToOpen(cards, requestId, documentId);
      // Null for a click on a row the last re-read removed. Nothing to show,
      // and nothing to report.
      if (!found) return;

      void openSpotlight(() => sign(found.documents), found.index);
    },
    [requests, documents, openSpotlight],
  );

  const answerClaim = useCallback(
    (claimId: string, body: { answer: VerificationAnswer; body?: string | null }) =>
      run(() => answerAffiliationClaim(claimId, body)),
    [run],
  );

  /**
   * Granting or declining a mandate somebody asked for.
   *
   * Through the same `run` as the other two, which re-reads the whole inbox
   * rather than patching the row — and it earns its cost here more than
   * anywhere: granting writes a mandate, so the work now has an agent, and the
   * row's own `canAnswer` is the server's answer to a question that has just
   * changed.
   */
  const answerRepresentation = useCallback(
    (requestId: string, body: { answer: RepresentationAnswer; note?: string | null }) =>
      run(() => answerRepresentationRequest(requestId, body)),
    [run],
  );

  /**
   * Bulk, and sequentially rather than in parallel.
   *
   * The order `writeDraftProvenance` uses, for its reason: one failure names one
   * request. Firing twenty at once would produce twenty results the user has to
   * reconcile, and the refusals here are ones they need to read — "somebody else
   * answered this" is per-row.
   */
  const answerMany = useCallback(
    (body: { answer: VerificationAnswer; reason: string }) => {
      const refusal = bulkRefusal({ ...body, selected });
      if (refusal) {
        setError(refusal);
        return Promise.resolve();
      }

      // The same string the sheet listed a moment ago — `cardTitle` over the
      // work — so the refusal names the row in the words the reader just read.
      const titleOf = (requestId: string) => {
        const found = (requests ?? []).find(
          (request) => request.requestId === requestId,
        );
        if (!found) return "One request";

        return `${cardTitle(found)} — ${found.workTitle ?? "Untitled"}`;
      };

      return run(async () => {
        // EVERY ROW IS ATTEMPTED, and the refusals are collected rather than
        // thrown. Awaiting bare inside `run` meant the first rejection left the
        // rest of the selection untouched and produced a single sentence naming
        // none of them — which is the opposite of what one-at-a-time is for.
        const failures: { title: string; message: string }[] = [];

        for (const requestId of selected) {
          try {
            await answerVerificationRequest(requestId, {
              answer: body.answer,
              body: body.reason || null,
            });
          } catch (cause: unknown) {
            failures.push({
              title: titleOf(requestId),
              message:
                cause instanceof ApiError
                  ? refusalFor(cause.status, cause.message)
                  : "That did not go through.",
            });
          }
        }

        // Thrown so `run` reloads and clears busy the way every other write
        // does. `ReportedRefusal` is what stops `run` rewriting a sentence that
        // has already named the rows.
        if (failures.length > 0) {
          throw new ReportedRefusal(bulkRefusalReport(failures, selected.length));
        }
      });
    },
    [run, selected, requests],
  );

  const toggle = useCallback((requestId: string) => {
    setSelected((current) =>
      current.includes(requestId)
        ? current.filter((id) => id !== requestId)
        : [...current, requestId],
    );
  }, []);

  const view = resolve({
    me,
    requests,
    claims,
    representation,
    documents,
    cases,
    caseLog,
    tab,
    status,
    aspect,
    selected,
    busy,
    error,
    loadError,
    focus,
  });

  const [images, setImages] = useState<Map<string, string>>(new Map());

  // Signed after the rows land, and never cached beyond this screen: a signature
  // expires in minutes, and a stale one renders as a broken image rather than an
  // error anybody can act on. A path storage refuses is simply absent from the
  // map, and the card shows its placeholder.
  // Both card kinds that show a photograph, in one pass: a verification card's
  // work and a representation request's. One signing round trip rather than
  // two, and the map is keyed by path so the two lists cannot disagree about
  // the same work.
  const paths =
    view.kind === "open"
      ? [
          ...view.rows.map((row) => row.work.imagePath),
          ...view.representation.map((row) => row.imagePath),
        ].filter((path): path is string => Boolean(path))
      : [];
  const pathKey = paths.join(",");

  useEffect(() => {
    if (pathKey.length === 0) return;

    // Nothing clears the map when the rows change. A signature is looked up by
    // path, so one belonging to a row that is no longer shown is never asked
    // for — and clearing it here would be a synchronous setState in an effect
    // to solve a problem that does not exist.
    let active = true;
    void signWorkImages(pathKey.split(",")).then((signed) => {
      if (active) setImages(signed);
    });

    return () => {
      active = false;
    };
  }, [pathKey]);

  /**
   * Takes the reader to the row a link asked for, once.
   *
   * Once, and that is what the ref is for: the rows re-render on every answer,
   * and a scroll that repeated would drag somebody back up the list mid-sentence
   * every time they typed. Keyed on the id, so a second notification opened from
   * the inbox does scroll.
   *
   * The card is reached through a ref the view attaches rather than by DOM id,
   * so this hook never queries the document.
   */
  const focused = view.kind === "open" ? view.focus : null;
  const scrolledTo = useRef<string | null>(null);
  const focusedCard = useRef<HTMLLIElement | null>(null);
  useEffect(() => {
    if (!focused || focused.missing || scrolledTo.current === focused.id) return;

    scrolledTo.current = focused.id;
    focusedCard.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [focused]);

  return {
    spotlight: spotlight.view,
    openDocument,
    closeDocument: spotlight.close,
    view,
    /** Signed photograph URLs by storage path, for every card that shows one. */
    images,
    /** For the view to attach to the card a link asked for; see the scroll effect. */
    focusedCard,
    setTab,
    setStatus,
    setAspect,
    toggle,
    clearSelection: () => setSelected([]),
    answer,
    answerMany,
    refer,
    addDocument,
    answerClaim,
    answerRepresentation,
    openCaseLog,
    askEvidence,
    extendTarget,
    assignReviewer,
    recordFeePaid,
    correctFee,
  };
}

/**
 * A card's documents, signed in one round trip.
 *
 * The whole set rather than the one clicked, so paging inside the viewer works.
 * A path missing from the map is a path storage refused — carried through as a
 * null URL, which the viewer renders as a sentence rather than a broken frame.
 * Nothing here makes an object URL, so there is nothing to release: every
 * document on this screen is one the registry already holds.
 */
async function sign(documents: DocumentRow[]): Promise<{ sources: SpotlightSource[] }> {
  const signed = await signProvenanceDocuments(
    documents.map((document) => document.objectPath),
  );

  return {
    sources: documents.map((document) => ({
      id: document.id,
      caption: document.name,
      contentType: document.contentType,
      url: signed.get(document.objectPath) ?? null,
    })),
  };
}

function resolve(input: {
  me: MeResponse | null;
  requests: VerificationRequestSummary[] | null;
  claims: AffiliationClaim[];
  representation: RepresentationRequestReceived[];
  documents: Record<string, ProvenanceDocument[]>;
  cases: AttributionServiceCase[];
  caseLog: Record<string, LogLine[]>;
  tab: VerificationTab;
  status: StatusFilter;
  aspect: AspectFilter;
  selected: string[];
  busy: boolean;
  error: string | null;
  loadError: string | null;
  focus: Focus | null;
}): VerificationsView {
  if (input.loadError) return { kind: "error", message: input.loadError };
  if (input.requests === null) return { kind: "loading" };

  const cards = input.requests.map((request) =>
    toCard(request, input.documents[request.entryId]),
  );
  const rows = visibleCards(cards, input.status, input.aspect);
  const claims = input.claims.map(toClaimCard);
  const representation = input.representation.map(toRepresentationCard);
  const cases = input.cases.map(toCaseCard);

  // ACROSS ALL THREE TABS. The banner asks how much is owed by the reader, not
  // how much is owed in whichever tab they have open — a number that moved as
  // they clicked between tabs would be answering a question nobody asked.
  const awaiting = awaitingCount(cards) + awaitingCount(claims) + awaitingCount(representation);

  return {
    kind: "open",
    tab: input.tab,
    counts: {
      requests: cards.length,
      affiliations: claims.length,
      representation: representation.length,
      service: cases.length,
      awaiting,
    },
    status: input.status,
    aspect: input.aspect,
    rows,
    claims,
    representation,
    cases,
    caseLog: input.caseLog,
    empty:
      input.tab === "service"
        ? cases.length === 0
          ? EMPTY_COPY.service
          : null
        : input.tab === "affiliations"
        ? claims.length === 0
          ? EMPTY_COPY.affiliations
          : null
        : input.tab === "representation"
          ? representation.length === 0
            ? EMPTY_COPY.representation
            : null
          : // The requests tab, and the only one with filters — so its empty
            // state has to tell "nothing was ever asked" apart from "your
            // filters match nothing", which `emptyFor` is for.
            emptyFor({
              total: cards.length,
              shown: rows.length,
              // The AWAITING COUNT FOR THIS TAB, not the banner's total. The
              // empty state says "you have answered everything asked of you",
              // and it must not be silenced by an unanswered claim in a tab the
              // reader is not looking at.
              awaiting: awaitingCount(cards),
            }),
    // Selection is dropped for rows the filters have hidden, so a bulk action
    // can never reach something the user is not looking at.
    selected: input.selected.filter((id) => rows.some((row) => row.requestId === id)),
    capacities: capacitiesFor(input.me),
    busy: input.busy,
    error: input.error,
    // Looked for among ALL the cards rather than the filtered rows: the effect
    // above has already widened the filters, and asking the narrowed list would
    // report a row as gone when it is merely hidden.
    focus: input.focus
      ? {
          id: input.focus.id,
          missing:
            input.focus.kind === "claim"
              ? !claims.some((claim) => claim.claimId === input.focus?.id)
              : input.focus.kind === "representation"
                ? !representation.some((row) => row.requestId === input.focus?.id)
                : !cards.some((card) => card.requestId === input.focus?.id),
        }
      : null,
  };
}
